import { Button, LottiePlayer } from '@repo/design-system-kit';
import checkInfo from '@repo/visual-assets/animations/check-info.lottie';
import generalSuccess from '@repo/visual-assets/animations/general_success.lottie';
import generalError from '@repo/visual-assets/illustrations/ill-cp-general-external-error.svg';
import styles from './feedback.module.css';

export enum FlowFeedbackStatus {
  Success = 'success',
  Pending = 'pending',
  Failure = 'failure',
}

export interface FeedbackAsset {
  type: 'lottie' | 'image' | 'dotlottie';
  path: string;
}

const feedbackAssets: Record<FlowFeedbackStatus, FeedbackAsset> = {
  [FlowFeedbackStatus.Success]: { type: 'lottie', path: generalSuccess },
  [FlowFeedbackStatus.Pending]: { type: 'lottie', path: checkInfo },
  [FlowFeedbackStatus.Failure]: { type: 'image', path: generalError },
};

interface FlowFeedbackProps {
  asset?: FeedbackAsset;
  feedback?: FlowFeedbackStatus;
  title: string;
  subtitle: React.ReactNode;
  primaryAction?: { label: string; onClick: () => void };
  secondaryAction?: { label: string; onClick: () => void };
}

export function FlowFeedback({
  asset: providedAsset,
  title,
  subtitle,
  primaryAction,
  secondaryAction,
  feedback,
}: FlowFeedbackProps): React.ReactNode {
  const asset = providedAsset || (feedback && feedbackAssets[feedback]);

  if (!asset) {
    return null;
  }

  const renderIllustration = () => {
    if (asset.type === 'lottie') {
      return <LottiePlayer data-test-lottie={asset.path} loop={false} path={asset.path} />;
    }

    if (asset.type === 'dotlottie') {
      return <LottiePlayer data-test-lottie={asset.path} loop={false} src={asset.path} />;
    }

    return <img alt="" data-test-image={asset.path} role="presentation" src={asset.path} />;
  };

  return (
    <main className={styles.wrapper}>
      <section className={styles.content}>
        <figure className={styles.illustration}>{renderIllustration()}</figure>
        <article className={styles.feedback}>
          <h1 className="heading-page">{title}</h1>
          <p className="body-prominent">{subtitle}</p>
        </article>
        <div className={styles.actions}>
          {primaryAction ? (
            <Button
              aria-label={primaryAction.label}
              data-feedback-action="primary"
              onPress={primaryAction.onClick}
              size="medium"
              variant="primary"
            >
              {primaryAction.label}
            </Button>
          ) : null}
          {secondaryAction ? (
            <Button
              aria-label={secondaryAction.label}
              data-feedback-action="secondary"
              onPress={secondaryAction.onClick}
              size="medium"
              variant="tertiary"
            >
              {secondaryAction.label}
            </Button>
          ) : null}
        </div>
      </section>
    </main>
  );
}
